import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import useAssessmentPreview from "../../assesments/hooks/useAssessmentPreview";
import useReportWizardConfig from "./useReportWizardConfig";
import useWizard from "../../../hooks/useWizard";
import useLang from "../../../hooks/useLang";
import ReportWizardSteps from "../ReportWizardSteps";
import useAnalysisData from "./useAnalysisData";

import useStepOne from "./useStepOne";
import useStepTwo from "./useStepTwo";
import useStepThree from "./useStepThree";

const useNewReportPage = () => {
  const lang = useLang();

  const firstLoadRef = useRef();
  const { analysisId: analysisIdFromParams } = useParams();
  const [analysisId, setAnalysisId] = useState(analysisIdFromParams);

  const {
    analysisState,
    createAnalysis,
    patchAnalysisFeatures,
    patchAnalysisConclusions,
  } = useAnalysisData({ analysisId, setAnalysisId });

  const { assessmentId } = useParams();
  const { title, articles } = useAssessmentPreview({
    assessmentId,
    showAnalyses: false,
  });
  const wizard = useWizard({ config: useReportWizardConfig() });
  const [isNavigating, setIsNavigating] = useState(false);

  const navigateWizard = async (idx) => {
    const current = wizard?.marker;
    if (current === idx) return null;
    if (analysisState !== "FINISHED") {
      setIsNavigating(true);
      try {
        if (current === 0 && idx === 1) {
          await patchAnalysisFeatures(selectedFeatures);
        }
        if (current === 1) {
          if (idx === 2) {
            await patchAnalysisConclusions(selectedConclusionOptions, true);
          } else {
            await patchAnalysisConclusions(selectedConclusionOptions);
          }
        }
        wizard?.handleGoTo(idx);
      } finally {
        setIsNavigating(false);
      }
    }
    wizard?.handleGoTo(idx);
  };

  const { stepOneArticles, selectedFeatures } = useStepOne({
    analysisId,
    setAnalysisId,
    navigateWizard,
    isNavigating,
  });
  const { stepTwoArticles, selectedConclusionOptions, selectedConclusions } =
    useStepTwo({
      analysisId,
      setAnalysisId,
      navigateWizard,
      isNavigating,
    });
  const { stepThreeArticles } = useStepThree({
    analysisId,
    setAnalysisId,
    selectedConclusions,
    navigateWizard,
    isNavigating,
  });

  const buttonCaptions = [
    lang("features.title"),
    lang("conclusions.title"),
    lang("reports.report"),
  ];

  const wizardButtons = wizard?.steps?.map((step, idx) => ({
    caption: buttonCaptions[idx],
    important: wizard?.marker === idx,
    onClick: () => {
      navigateWizard(idx);
    },
    disabled:
      (idx > 0 &&
        (!selectedFeatures ||
          (selectedFeatures?.value &&
            selectedFeatures?.value?.length === 0))) ||
      (analysisState !== "FINISHED" && idx - wizard?.marker > 1),
  }));

  const stepArticles = [stepOneArticles, stepTwoArticles, stepThreeArticles];

  let page = { hasAccess: true };

  page.header = { title };
  page.articles = [
    ...articles,
    <ReportWizardSteps
      wizardButtons={wizardButtons}
      isNavigating={isNavigating}
    />,
    ...stepArticles[wizard?.marker],
  ];
  /*
    page.rightPanel = {
      articles: [
        <ReportWizzardSteps wizardButtons={wizardButtons} />,
        ...stepArticles[wizard?.marker],
      ],
    };
    */

  useEffect(() => {
    if ((firstLoadRef.current = true)) {
      if (analysisState === "CONCLUSION_SELECTION") wizard?.handleGoTo(1);
      if (analysisState === "FINISHED") wizard?.handleGoTo(2);
    }
    firstLoadRef.current = false;
    // eslint-disable-next-line
  }, [analysisState]);

  useEffect(() => {
    firstLoadRef.current = true;
    if (analysisIdFromParams == null) createAnalysis();
    // eslint-disable-next-line
  }, []);

  return page;
};

export default useNewReportPage;
